/**
 * Skeleton loader for the task list — shown while tasks are loading.
 * Mimics the real list layout to prevent layout shift.
 */

import clsx from 'clsx';

interface TaskListSkeletonProps {
  rows?: number;
  showHeader?: boolean;
}

function SkeletonRow({ index }: { index: number }) {
  // Vary widths for a more natural look
  const titleWidths = ['w-[45%]', 'w-[55%]', 'w-[35%]', 'w-[50%]', 'w-[40%]', 'w-[60%]'];
  const titleWidth = titleWidths[index % titleWidths.length];

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border-primary/30">
      {/* Status icon */}
      <div className="w-4 h-4 rounded-full bg-surface-hover animate-pulse shrink-0" />
      
      {/* Priority dot */}
      <div className="w-2 h-2 rounded-full bg-surface-hover animate-pulse shrink-0" />
      
      {/* Title */}
      <div className={clsx('h-3.5 rounded bg-surface-hover animate-pulse', titleWidth)} />
      
      {/* Spacer */}
      <div className="flex-1" />
      
      {/* Tags placeholder */}
      {index % 3 === 0 && (
        <div className="w-14 h-5 rounded-full bg-surface-hover animate-pulse shrink-0" />
      )}
      
      {/* Assignee avatar */}
      <div className="w-6 h-6 rounded-full bg-surface-hover animate-pulse shrink-0" />
      
      {/* Date */}
      <div className="w-16 h-3 rounded bg-surface-hover animate-pulse shrink-0" />
    </div>
  );
}

export function TaskListSkeleton({ rows = 12, showHeader = true }: TaskListSkeletonProps) {
  return (
    <div className="animate-fade-in">
      {showHeader && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border-primary/50">
          <div className="w-3 h-3 rounded-full bg-surface-hover animate-pulse" />
          <div className="w-20 h-3 rounded bg-surface-hover animate-pulse" />
          <div className="w-6 h-4 rounded bg-surface-hover animate-pulse ml-1" />
        </div>
      )}
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} index={i} />
      ))}
    </div>
  );
}
